import type { WatchChannelCommand, WatchChannelMessage, WatchCommand, WatchCommandInput, WatchInvite, WatchMedia, WatchPlaybackState, WatchSessionMessage, WatchSessionSnapshot, WatchUser } from '../../shared/watch-together'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { api, ApiError, apiWebSocketUrl } from '@/services/api'
import { projectedWatchPosition } from '../../shared/watch-together'
import { useAppStore } from './app'

type ConnectionState = 'idle' | 'connecting' | 'connected' | 'reconnecting'
type RemoteSeekGesture = Extract<WatchSessionMessage, { type: 'gesture' }>['feedback']

export const useWatchTogetherStore = defineStore('watch-together', () => {
    const app = useAppStore()
    const users = ref<WatchUser[]>([])
    const recentUsers = ref<WatchUser[]>([])
    const usersLoading = ref(false)
    const usersLoadingMore = ref(false)
    const usersHasMore = ref(false)
    const usersQuery = ref('')
    const invitations = ref<WatchInvite[]>([])
    const activeSession = ref<WatchSessionSnapshot>()
    const channelState = ref<ConnectionState>('idle')
    const sessionState = ref<ConnectionState>('idle')
    const notice = ref('')
    const resyncRequest = ref(0)
    const forcedPlayerExitRequest = ref(0)
    const snapshotGeneration = ref(0)
    const controllerEpoch = ref(0)
    const isController = ref(false)
    const oneWayDelayMs = ref(0)
    const remoteSeekGesture = ref<RemoteSeekGesture>()
    const remoteSeekGestureGeneration = ref(0)
    let channelSocket: WebSocket | undefined
    let sessionSocket: WebSocket | undefined
    let channelRetryTimer: number | undefined
    let sessionRetryTimer: number | undefined
    let channelRetries = 0
    let sessionRetries = 0
    let pingTimer: number | undefined
    let sessionRecovery: Promise<boolean> | undefined
    const latencySamples: number[] = []
    let bestClockRoundTripMs = Number.POSITIVE_INFINITY
    let serverClockOffsetMs: number | undefined
    let channelEnabled = false
    let sessionEnabled = false
    let userDirectoryRequest = 0
    let foregroundListenersStarted = false
    let pendingAcceptedSession: WatchSessionSnapshot | undefined

    const isHost = computed(() => Boolean(activeSession.value && activeSession.value.hostId === app.user?.id))
    const canControl = computed(() => isHost.value && isController.value && sessionState.value === 'connected')
    const connected = computed(() => sessionState.value === 'connected')
    const availableUsers = computed(() => {
        const occupied = new Set([
            ...(activeSession.value?.participants.map(user => user.id) || []),
            ...(activeSession.value?.invited.map(user => user.id) || []),
        ])
        return users.value.filter(user => !occupied.has(user.id))
    })
    const availableRecentUsers = computed(() => {
        const occupied = new Set([
            ...(activeSession.value?.participants.map(user => user.id) || []),
            ...(activeSession.value?.invited.map(user => user.id) || []),
        ])
        return recentUsers.value.filter(user => !occupied.has(user.id))
    })

    function seedServerClock(serverSentAt: number) {
        if (serverClockOffsetMs === undefined) { serverClockOffsetMs = serverSentAt - Date.now() }
    }

    function estimatedMessageAge(serverSentAt: number): number {
        if (serverClockOffsetMs === undefined) { return oneWayDelayMs.value }
        return Math.max(0, Date.now() - (serverSentAt - serverClockOffsetMs))
    }

    function localizePlayback(state: WatchPlaybackState, serverSentAt = state.updatedAt): WatchPlaybackState {
        // The server projects to send time. Include transit and any foreground dispatch delay before rebasing.
        const messageAge = estimatedMessageAge(serverSentAt)
        const position = state.paused
            ? state.position
            : projectedWatchPosition(state, Math.max(state.updatedAt, serverSentAt) + messageAge)
        const receivedAt = Date.now()
        return {
            ...state,
            position,
            updatedAt: receivedAt,
            temporaryRate: state.temporaryRate
                ? {
                        holdId: state.temporaryRate.holdId,
                        basePlaybackRate: state.temporaryRate.basePlaybackRate,
                        expiresAt: receivedAt + Math.max(0, state.temporaryRate.expiresAt - serverSentAt - messageAge),
                    }
                : undefined,
        }
    }

    function localizeSession(session: WatchSessionSnapshot, serverSentAt = session.playback.updatedAt): WatchSessionSnapshot {
        return { ...session, playback: localizePlayback(session.playback, serverSentAt) }
    }

    function mergeLocalizedSession(session: WatchSessionSnapshot, serverSentAt = session.playback.updatedAt): WatchSessionSnapshot {
        const localized = localizeSession(session, serverSentAt)
        const current = activeSession.value
        if (!current || current.id !== localized.id) { return localized }
        if (localized.playback.version > current.playback.version) { return localized }
        if (localized.playback.version === current.playback.version) {
            const currentHold = current.playback.temporaryRate
            const localizedHold = localized.playback.temporaryRate
            if (currentHold && !localizedHold) {
                return {
                    ...localized,
                    media: {
                        ...localized.media,
                        episodeIndex: current.media.episodeIndex,
                        episodeLabel: current.media.episodeLabel,
                    },
                    playback: current.playback,
                }
            }
            if (currentHold && localizedHold?.holdId === currentHold.holdId) {
                localized.playback.temporaryRate = {
                    ...localizedHold,
                    expiresAt: Math.max(currentHold.expiresAt, localizedHold.expiresAt),
                }
            }
            return localized
        }
        return {
            ...localized,
            media: {
                ...localized.media,
                episodeIndex: current.media.episodeIndex,
                episodeLabel: current.media.episodeLabel,
            },
            playback: current.playback,
        }
    }

    function clearRemoteSeekGesture() {
        remoteSeekGesture.value = undefined
        remoteSeekGestureGeneration.value += 1
    }

    function resolveUser(user: WatchUser): WatchUser {
        const known = user.id === app.user?.id
            ? app.user
            : [...users.value, ...recentUsers.value].find(candidate => candidate.id === user.id)
        return {
            ...user,
            username: user.username || known?.username,
            displayName: known?.displayName || user.displayName,
            avatar: known?.avatar || user.avatar,
        }
    }

    function closeSocket(socket: WebSocket | undefined) {
        if (!socket) { return }
        socket.onclose = null
        socket.onerror = null
        socket.onmessage = null
        try { socket.close(1000, 'closed') }
        catch { /* A socket may still be completing its initial handshake. */ }
    }

    function retryDelay(attempt: number) {
        return Math.min(30_000, 750 * 2 ** Math.min(attempt, 5)) + Math.floor(Math.random() * 300)
    }

    function clearSession(reason?: string) {
        sessionEnabled = false
        window.clearTimeout(sessionRetryTimer)
        window.clearInterval(pingTimer)
        closeSocket(sessionSocket)
        sessionSocket = undefined
        sessionState.value = 'idle'
        activeSession.value = undefined
        clearRemoteSeekGesture()
        isController.value = false
        controllerEpoch.value = 0
        latencySamples.length = 0
        oneWayDelayMs.value = 0
        bestClockRoundTripMs = Number.POSITIVE_INFINITY
        serverClockOffsetMs = undefined
        if (foregroundListenersStarted) {
            window.removeEventListener('focus', onSessionForeground)
            document.removeEventListener('visibilitychange', onSessionForeground)
            foregroundListenersStarted = false
        }
        if (reason) { notice.value = reason }
    }

    function onSessionForeground() {
        if (document.visibilityState !== 'hidden') {
            clearRemoteSeekGesture()
            claimControl()
        }
    }

    function startForegroundListeners() {
        if (foregroundListenersStarted) { return }
        foregroundListenersStarted = true
        window.addEventListener('focus', onSessionForeground)
        document.addEventListener('visibilitychange', onSessionForeground)
    }

    function handleSessionMessage(message: WatchSessionMessage) {
        if (message.type === 'snapshot') {
            seedServerClock(message.serverSentAt)
            clearRemoteSeekGesture()
            snapshotGeneration.value += 1
            activeSession.value = mergeLocalizedSession(message.session, message.serverSentAt)
            controllerEpoch.value = message.controller.epoch
            isController.value = message.controller.isController
            return
        }
        if (message.type === 'state' && activeSession.value) {
            // Every state broadcast represents a versioned mutation. Equal-version
            // duplicates must not shorten a rate-hold lease renewed out of band.
            if (message.state.version <= activeSession.value.playback.version) { return }
            seedServerClock(message.serverSentAt)
            const playback = localizePlayback(message.state, message.serverSentAt)
            activeSession.value = { ...activeSession.value, playback, media: { ...activeSession.value.media, episodeIndex: playback.episodeIndex, episodeLabel: playback.episodeLabel } }
            return
        }
        if (message.type === 'rate-hold-renewed' && activeSession.value) {
            seedServerClock(message.serverSentAt)
            const current = activeSession.value.playback
            if (current.version !== message.playbackVersion || current.temporaryRate?.holdId !== message.holdId) { return }
            const receivedAt = Date.now()
            const messageAge = estimatedMessageAge(message.serverSentAt)
            const renewedExpiresAt = receivedAt + Math.max(0, message.expiresAt - message.serverSentAt - messageAge)
            activeSession.value = {
                ...activeSession.value,
                playback: {
                    ...current,
                    temporaryRate: {
                        ...current.temporaryRate,
                        expiresAt: Math.max(current.temporaryRate.expiresAt, renewedExpiresAt),
                    },
                },
            }
            return
        }
        if (message.type === 'gesture') {
            seedServerClock(message.serverSentAt)
            remoteSeekGesture.value = message.feedback
            remoteSeekGestureGeneration.value += 1
            return
        }
        if (message.type === 'control') {
            controllerEpoch.value = message.controller.epoch
            isController.value = message.controller.isController
            if (!message.controller.isController) { resyncRequest.value += 1 }
            return
        }
        if (message.type === 'pong') {
            const receivedAt = Date.now()
            const roundTrip = Math.max(0, receivedAt - message.clientSentAt)
            latencySamples.push(roundTrip)
            if (latencySamples.length > 8) { latencySamples.shift() }
            oneWayDelayMs.value = Math.min(...latencySamples) / 2
            if (roundTrip <= bestClockRoundTripMs) {
                bestClockRoundTripMs = roundTrip
                serverClockOffsetMs = message.serverSentAt - (message.clientSentAt + roundTrip / 2)
            }
            return
        }
        if (message.type === 'participants' && activeSession.value) {
            activeSession.value = { ...activeSession.value, participants: message.participants, invited: message.invited }
            return
        }
        if (message.type === 'ended') { clearSession(message.reason); return }
        if (message.type === 'kicked') {
            forcedPlayerExitRequest.value += 1
            clearSession('发起人已将你移出联动观影')
            return
        }
        if (message.type === 'error') { notice.value = message.message }
    }

    function sendSessionRaw(command: WatchCommand): boolean {
        if (sessionSocket?.readyState !== WebSocket.OPEN) { return false }
        sessionSocket.send(JSON.stringify(command))
        return true
    }

    function claimControl() {
        if (!isHost.value || isController.value || document.visibilityState === 'hidden') { return false }
        return sendSessionRaw({ type: 'claim-control', commandId: crypto.randomUUID() })
    }

    function pingSession() {
        sendSessionRaw({ type: 'ping', id: crypto.randomUUID(), clientSentAt: Date.now() })
    }

    async function recoverSessionSnapshot(sessionId: string): Promise<boolean> {
        if (sessionRecovery) { return sessionRecovery }
        sessionRecovery = (async () => {
            try {
                const result = await api.watchSession(sessionId)
                if (!sessionEnabled || activeSession.value?.id !== sessionId) { return false }
                snapshotGeneration.value += 1
                activeSession.value = mergeLocalizedSession(result.session)
                return true
            }
            catch (error) {
                if (error instanceof ApiError && [403, 404, 410].includes(error.status)) {
                    forcedPlayerExitRequest.value += 1
                    clearSession(error.message)
                }
                return false
            }
            finally { sessionRecovery = undefined }
        })()
        return sessionRecovery
    }

    function connectSessionSocket() {
        const sessionId = activeSession.value?.id
        if (!sessionId || !sessionEnabled || !app.authenticated) { return }
        closeSocket(sessionSocket)
        sessionState.value = sessionRetries ? 'reconnecting' : 'connecting'
        const socket = new WebSocket(apiWebSocketUrl(`/watch/sessions/${encodeURIComponent(sessionId)}/socket`))
        sessionSocket = socket
        socket.onopen = () => {
            if (sessionSocket !== socket) { return }
            sessionRetries = 0
            sessionState.value = 'connected'
            window.clearInterval(pingTimer)
            pingSession()
            pingTimer = window.setInterval(pingSession, 10_000)
            claimControl()
        }
        socket.onmessage = (event) => {
            if (sessionSocket !== socket || typeof event.data !== 'string') { return }
            try { handleSessionMessage(JSON.parse(event.data) as WatchSessionMessage) }
            catch { notice.value = '收到的联动状态无效' }
        }
        socket.onerror = () => socket.close()
        socket.onclose = () => {
            if (sessionSocket !== socket || !sessionEnabled || activeSession.value?.id !== sessionId) { return }
            window.clearInterval(pingTimer)
            isController.value = false
            clearRemoteSeekGesture()
            if (sessionRetries >= 8) {
                void recoverSessionSnapshot(sessionId).then((restored) => {
                    if (restored && sessionEnabled) {
                        sessionRetries = 0
                        connectSessionSocket()
                        return
                    }
                    if (sessionEnabled) {
                        sessionEnabled = false
                        sessionState.value = 'idle'
                        notice.value = '联动连接持续失败，请稍后重新进入'
                    }
                })
                return
            }
            sessionState.value = 'reconnecting'
            if (sessionRetries === 2 || sessionRetries === 5) { void recoverSessionSnapshot(sessionId) }
            sessionRetryTimer = window.setTimeout(connectSessionSocket, retryDelay(sessionRetries++))
        }
    }

    function useSession(session: WatchSessionSnapshot) {
        if (activeSession.value?.id !== session.id) { clearSession() }
        snapshotGeneration.value += 1
        activeSession.value = mergeLocalizedSession(session)
        sessionEnabled = true
        sessionRetries = 0
        startForegroundListeners()
        connectSessionSocket()
    }

    function handleChannelMessage(message: WatchChannelMessage) {
        if (message.type === 'invitations') {
            invitations.value = message.invitations.filter(item => item.expiresAt > Date.now())
            return
        }
        if (message.type === 'invite') {
            invitations.value = [message.invitation, ...invitations.value.filter(item => item.sessionId !== message.invitation.sessionId)]
            return
        }
        if (message.type === 'session-removed') {
            if (channelSocket?.readyState === WebSocket.OPEN) {
                channelSocket.send(JSON.stringify({ type: 'ack', eventId: message.eventId } satisfies WatchChannelCommand))
            }
            invitations.value = invitations.value.filter(item => item.sessionId !== message.sessionId)
            if (activeSession.value?.id === message.sessionId) {
                forcedPlayerExitRequest.value += 1
                clearSession(message.reason)
            }
            return
        }
        invitations.value = invitations.value.filter(item => item.sessionId !== message.sessionId)
    }

    function connectChannel() {
        if (!channelEnabled || !app.authenticated) { return }
        closeSocket(channelSocket)
        channelState.value = channelRetries ? 'reconnecting' : 'connecting'
        const socket = new WebSocket(apiWebSocketUrl('/watch/channel'))
        channelSocket = socket
        socket.onopen = () => {
            if (channelSocket !== socket) { return }
            channelRetries = 0
            channelState.value = 'connected'
        }
        socket.onmessage = (event) => {
            if (channelSocket !== socket || typeof event.data !== 'string') { return }
            try { handleChannelMessage(JSON.parse(event.data) as WatchChannelMessage) }
            catch { /* Ignore malformed channel messages. */ }
        }
        socket.onerror = () => socket.close()
        socket.onclose = () => {
            if (channelSocket !== socket || !channelEnabled) { return }
            if (channelRetries >= 8) {
                channelEnabled = false
                channelState.value = 'idle'
                notice.value = '邀请通道连接失败，将在下次刷新时重试'
                return
            }
            channelState.value = 'reconnecting'
            channelRetryTimer = window.setTimeout(connectChannel, retryDelay(channelRetries++))
        }
    }

    function startChannel() {
        if (channelEnabled || !app.authenticated) { return }
        channelEnabled = true
        channelRetries = 0
        connectChannel()
    }

    function stopAll() {
        channelEnabled = false
        window.clearTimeout(channelRetryTimer)
        closeSocket(channelSocket)
        channelSocket = undefined
        channelState.value = 'idle'
        invitations.value = []
        users.value = []
        recentUsers.value = []
        usersLoading.value = false
        usersLoadingMore.value = false
        usersHasMore.value = false
        usersQuery.value = ''
        userDirectoryRequest += 1
        pendingAcceptedSession = undefined
        clearSession()
        notice.value = ''
    }

    async function loadUsers(force = false, query = usersQuery.value) {
        const normalizedQuery = query.trim().replace(/^@/u, '').slice(0, 64)
        if (!force && normalizedQuery === usersQuery.value && users.value.length) { return }
        const requestId = ++userDirectoryRequest
        usersLoading.value = true
        usersLoadingMore.value = false
        try {
            const result = await api.watchUsers(normalizedQuery, 1)
            if (requestId !== userDirectoryRequest) { return }
            users.value = result.users
            recentUsers.value = normalizedQuery ? [] : result.recent
            usersHasMore.value = result.hasMore
            usersQuery.value = normalizedQuery
        }
        finally {
            if (requestId === userDirectoryRequest) { usersLoading.value = false }
        }
    }

    async function loadMoreUsers() {
        if (usersLoading.value || usersLoadingMore.value || !usersHasMore.value) { return }
        const requestId = userDirectoryRequest
        const nextPage = Math.floor(users.value.length / 24) + 1
        usersLoadingMore.value = true
        try {
            const result = await api.watchUsers(usersQuery.value, nextPage)
            if (requestId !== userDirectoryRequest || result.page !== nextPage) { return }
            const known = new Set(users.value.map(user => user.id))
            users.value.push(...result.users.filter(user => !known.has(user.id)))
            usersHasMore.value = result.hasMore
        }
        finally {
            if (requestId === userDirectoryRequest) { usersLoadingMore.value = false }
        }
    }

    async function createSession(media: WatchMedia, playback: Partial<WatchPlaybackState>, userIds: string[]) {
        if (activeSession.value) { throw new Error('当前已有正在进行的联动观影') }
        const result = await api.createWatchSession({ media, playback, userIds, creationId: crypto.randomUUID() })
        useSession(result.session)
        return result.session
    }

    async function acceptInvite(invitation: WatchInvite) {
        if (activeSession.value && activeSession.value.id !== invitation.sessionId) { throw new Error('请先结束当前联动观影') }
        const result = await api.acceptWatchInvite(invitation.sessionId)
        invitations.value = invitations.value.filter(item => item.sessionId !== invitation.sessionId)
        pendingAcceptedSession = result.session
        return result.session
    }

    async function rejectInvite(invitation: WatchInvite) {
        await api.rejectWatchInvite(invitation.sessionId)
        invitations.value = invitations.value.filter(item => item.sessionId !== invitation.sessionId)
    }

    async function restoreSession(sessionId: string) {
        if (!sessionId) { return undefined }
        if (activeSession.value && activeSession.value.id !== sessionId) { throw new Error('当前已有正在进行的联动观影') }
        if (activeSession.value?.id === sessionId) {
            if (!sessionEnabled) { sessionEnabled = true; connectSessionSocket() }
            return activeSession.value
        }
        if (pendingAcceptedSession?.id === sessionId) {
            const session = pendingAcceptedSession
            pendingAcceptedSession = undefined
            useSession(session)
            return activeSession.value
        }
        const result = await api.watchSession(sessionId)
        useSession(result.session)
        return result.session
    }

    async function inviteUsers(userIds: string[]) {
        const session = activeSession.value
        if (!session) { return }
        const occupied = new Set([...session.participants, ...session.invited].map(user => user.id))
        const candidates = [...users.value, ...recentUsers.value]
        const optimistic = userIds
            .filter(id => !occupied.has(id))
            .map(id => candidates.find(user => user.id === id))
            .filter((user): user is WatchUser => Boolean(user))
        const optimisticIds = new Set(optimistic.map(user => user.id))
        if (optimistic.length) {
            activeSession.value = { ...session, invited: [...session.invited, ...optimistic] }
        }
        try {
            const result = await api.inviteWatchUsers(session.id, userIds)
            if (activeSession.value?.id === session.id) { activeSession.value = mergeLocalizedSession(result.session) }
        }
        catch (error) {
            const current = activeSession.value
            if (current?.id === session.id && optimisticIds.size) {
                activeSession.value = {
                    ...current,
                    invited: current.invited.filter(user => !optimisticIds.has(user.id)),
                }
            }
            throw error
        }
    }

    async function kickUser(userId: string) {
        if (!activeSession.value) { return }
        const result = await api.kickWatchUser(activeSession.value.id, userId)
        activeSession.value = mergeLocalizedSession(result.session)
    }

    async function leave() {
        if (!activeSession.value) { return }
        const id = activeSession.value.id
        const request = api.leaveWatchSession(id)
        clearSession()
        await request
    }

    async function end(reason = '发起人结束了联动') {
        if (!activeSession.value) { return }
        const id = activeSession.value.id
        await api.endWatchSession(id, reason)
        clearSession()
    }

    function send(command: WatchCommandInput) {
        if (command.type === 'presence' || command.type === 'claim-control' || command.type === 'ping') {
            return sendSessionRaw(command)
        }
        if (!canControl.value) { return false }
        const wireCommand: WatchCommand = {
            ...command,
            commandId: crypto.randomUUID(),
            controllerEpoch: controllerEpoch.value,
        } as WatchCommand
        return sendSessionRaw(wireCommand)
    }

    function requestResync() {
        resyncRequest.value += 1
    }

    function clearNotice() {
        notice.value = ''
    }

    return {
        users,
        recentUsers,
        usersLoading,
        usersLoadingMore,
        usersHasMore,
        usersQuery,
        availableUsers,
        availableRecentUsers,
        invitations,
        activeSession,
        channelState,
        sessionState,
        connected,
        isHost,
        canControl,
        isController,
        snapshotGeneration,
        oneWayDelayMs,
        remoteSeekGesture,
        remoteSeekGestureGeneration,
        notice,
        resyncRequest,
        forcedPlayerExitRequest,
        startChannel,
        stopAll,
        loadUsers,
        loadMoreUsers,
        createSession,
        acceptInvite,
        rejectInvite,
        restoreSession,
        inviteUsers,
        kickUser,
        leave,
        end,
        send,
        claimControl,
        requestResync,
        clearNotice,
        clearSession,
        resolveUser,
    }
})
